import {
    type GetWorkspaceCapabilityConfigurationType,
    type GetWorkspaceCapabilityUdoBindingsType,
    WorkspaceCapabilityType,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {workspaceCapabilityUdoBindingsValidatorMap} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityUdoBindingsValidator";
import {workspaceCapabilityConfigurationValidatorMap} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityConfigurationValidator";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

// TODO: WorkspaceCapabilityType vs GetWorkspaceCapabilityConfigurationType: the word `Type` is
//       overloaded here and gets confusing real fast. Think about it some more.
// TODO: Since I have made this a generic, I am unable to use WorkspaceCapability
//       without introducing a generic. This is leading to a lot of unnecessary code.
//       Try to fix this in the future.
// TODO: Find a way of enforcing exhaustiveness via typescript here
export type WorkspaceCapability<T extends WorkspaceCapabilityType = WorkspaceCapabilityType> = {
    configuration: GetWorkspaceCapabilityConfigurationType<T>;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    id: Uuid;
    revisionId: Uuid;
    status: WorkspaceCapabilityStatus;
    type: T;
    udoBindings: GetWorkspaceCapabilityUdoBindingsType<T>;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
    workspaceId: Uuid;
};

export type AnyWorkspaceCapability = {
    [T in WorkspaceCapabilityType]: WorkspaceCapability<T>;
}[WorkspaceCapabilityType];

export enum WorkspaceCapabilityStatus {
    disabled = "4e1f337a-6d61-4e51-9a0f-af3b079f2570",
    enabled = "c2eb18cf-b76d-4d62-9490-38185d9962be",
};

const workspaceCapabilityBaseValidator = zod.object({
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    id: uuidZodValidator,
    revisionId: uuidZodValidator,
    status: zod.enum(WorkspaceCapabilityStatus),
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    workspaceId: uuidZodValidator,
});

const workspaceCapabilityValidatorMap = {
    [WorkspaceCapabilityType.eventContainerMovement]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.eventContainerMovement),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.eventContainerMovement],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.eventContainerMovement],
    }),
    [WorkspaceCapabilityType.eventContainerAndEventOrchestration]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.eventContainerAndEventOrchestration),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.eventContainerAndEventOrchestration],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.eventContainerAndEventOrchestration],
    }),
    [WorkspaceCapabilityType.agentView]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.agentView),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.agentView],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.agentView],
    }),
    [WorkspaceCapabilityType.amaronSpecificBehaviour]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.amaronSpecificBehaviour),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.amaronSpecificBehaviour],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.amaronSpecificBehaviour],
    }),
    [WorkspaceCapabilityType.duluxSpecificBehaviour]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.duluxSpecificBehaviour),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.duluxSpecificBehaviour],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.duluxSpecificBehaviour],
    }),
    [WorkspaceCapabilityType.analyticalEngine]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.analyticalEngine),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.analyticalEngine],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.analyticalEngine],
    }),
    [WorkspaceCapabilityType.ottopilotEvents]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotEvents),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotEvents],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotEvents],
    }),
    [WorkspaceCapabilityType.ottopilotEventContainers]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotEventContainers),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotEventContainers],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotEventContainers],
    }),
    [WorkspaceCapabilityType.ottopilotCustomers]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotCustomers),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotCustomers],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotCustomers],
    }),
    [WorkspaceCapabilityType.ottopilotAnnouncements]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotAnnouncements),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotAnnouncements],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotAnnouncements],
    }),
    [WorkspaceCapabilityType.ottopilotBtlActivities]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotBtlActivities),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotBtlActivities],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotBtlActivities],
    }),
    [WorkspaceCapabilityType.ottopilotContests]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotContests),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotContests],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotContests],
    }),
    // [WorkspaceCapabilityType.ottopilotJourneys]: workspaceCapabilityBaseValidator.extend({
    //     type: zod.literal(WorkspaceCapabilityType.ottopilotJourneys),
    //     configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotJourneys],
    // }),
    [WorkspaceCapabilityType.ottopilotAnalytics]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotAnalytics),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotAnalytics],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotAnalytics],
    }),
    [WorkspaceCapabilityType.ottopilotBobcard]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotBobcard),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotBobcard],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotBobcard],
    }),
    [WorkspaceCapabilityType.ottopilotExternalSource]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotExternalSource),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotExternalSource],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotExternalSource],
    }),
    [WorkspaceCapabilityType.ottopilotGeographicNodes]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotGeographicNodes),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotGeographicNodes],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotGeographicNodes],
    }),
    [WorkspaceCapabilityType.ottopilotLeaderboard]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotLeaderboard),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotLeaderboard],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotLeaderboard],
    }),
    [WorkspaceCapabilityType.ottopilotOtp]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotOtp),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotOtp],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotOtp],
    }),
    [WorkspaceCapabilityType.ottopilotTargets]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotTargets),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotTargets],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotTargets],
    }),
    [WorkspaceCapabilityType.ottopilotWebhooks]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottopilotWebhooks),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottopilotWebhooks],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotWebhooks],
    }),
    [WorkspaceCapabilityType.assets]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.assets),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.assets],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.assets],
    }),
    [WorkspaceCapabilityType.canonicals]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.canonicals),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.canonicals],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.canonicals],
    }),
    [WorkspaceCapabilityType.ottosyncDataSources]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottosyncDataSources),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottosyncDataSources],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottosyncDataSources],
    }),
    [WorkspaceCapabilityType.mediaLibrary]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.mediaLibrary),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.mediaLibrary],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.mediaLibrary],
    }),
    [WorkspaceCapabilityType.blogs]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.blogs),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.blogs],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.blogs],
    }),
    [WorkspaceCapabilityType.duluxLocationPages]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.duluxLocationPages),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.duluxLocationPages],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.duluxLocationPages],
    }),
    [WorkspaceCapabilityType.organizationalUnitTemplatesAndOrganizationalUnits]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.organizationalUnitTemplatesAndOrganizationalUnits),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.organizationalUnitTemplatesAndOrganizationalUnits],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.organizationalUnitTemplatesAndOrganizationalUnits],
    }),
    [WorkspaceCapabilityType.subscription]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.subscription),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.subscription],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.subscription],
    }),
    [WorkspaceCapabilityType.productCatalogueTemplatesAndProductCatalogues]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.productCatalogueTemplatesAndProductCatalogues),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.productCatalogueTemplatesAndProductCatalogues],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.productCatalogueTemplatesAndProductCatalogues],
    }),
    [WorkspaceCapabilityType.productInventory]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.productInventory),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.productInventory],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.productInventory],
    }),
    [WorkspaceCapabilityType.tuffexProductInventory]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.tuffexProductInventory),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.tuffexProductInventory],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.tuffexProductInventory],
    }),
    [WorkspaceCapabilityType.tuffexProductCollection]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.tuffexProductCollection),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.tuffexProductCollection],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.tuffexProductCollection],
    }),
    [WorkspaceCapabilityType.reportCollections]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.reportCollections),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.reportCollections],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.reportCollections],
    }),
    [WorkspaceCapabilityType.reportTemplatesAndReports]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.reportTemplatesAndReports),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.reportTemplatesAndReports],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.reportTemplatesAndReports],
    }),
    [WorkspaceCapabilityType.retailLocations]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.retailLocations),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.retailLocations],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.retailLocations],
    }),
    [WorkspaceCapabilityType.retailLocationsProductCatalogue]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.retailLocationsProductCatalogue),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.retailLocationsProductCatalogue],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.retailLocationsProductCatalogue],
    }),
    [WorkspaceCapabilityType.retailLocationMicroStore]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.retailLocationMicroStore),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.retailLocationMicroStore],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.retailLocationMicroStore],
    }),
    [WorkspaceCapabilityType.storefrontCustomerProfiles]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.storefrontCustomerProfiles),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.storefrontCustomerProfiles],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.storefrontCustomerProfiles],
    }),
    [WorkspaceCapabilityType.ottokartLeads]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottokartLeads),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottokartLeads],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottokartLeads],
    }),
    [WorkspaceCapabilityType.ottokartCustomerEngagement]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.ottokartCustomerEngagement),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.ottokartCustomerEngagement],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottokartCustomerEngagement],
    }),
    [WorkspaceCapabilityType.tuffexProductCatalogue]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.tuffexProductCatalogue),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.tuffexProductCatalogue],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.tuffexProductCatalogue],
    }),
    [WorkspaceCapabilityType.storefrontAuthentication]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.storefrontAuthentication),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.storefrontAuthentication],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.storefrontAuthentication],
    }),
    [WorkspaceCapabilityType.commerceTransactions]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.commerceTransactions),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.commerceTransactions],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.commerceTransactions],
    }),
    [WorkspaceCapabilityType.paymentGateway]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.paymentGateway),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.paymentGateway],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.paymentGateway],
    }),
    [WorkspaceCapabilityType.flexibleFitmentEngine]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.flexibleFitmentEngine),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.flexibleFitmentEngine],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.flexibleFitmentEngine],
    }),
    [WorkspaceCapabilityType.b2bQuotationEngine]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.b2bQuotationEngine),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.b2bQuotationEngine],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.b2bQuotationEngine],
    }),
    [WorkspaceCapabilityType.customerAuthentication]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.customerAuthentication),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.customerAuthentication],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.customerAuthentication],
    }),
    [WorkspaceCapabilityType.domainAggregate]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.domainAggregate),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.domainAggregate],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.domainAggregate],
    }),
    [WorkspaceCapabilityType.stableLinks]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.stableLinks),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.stableLinks],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.stableLinks],
    }),
    [WorkspaceCapabilityType.marketplaceIntelligence]: workspaceCapabilityBaseValidator.extend({
        type: zod.literal(WorkspaceCapabilityType.marketplaceIntelligence),
        configuration: workspaceCapabilityConfigurationValidatorMap[WorkspaceCapabilityType.marketplaceIntelligence],
        udoBindings: workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.marketplaceIntelligence],
    }),
} satisfies {
    [T in WorkspaceCapabilityType]: zod.ZodType<WorkspaceCapability<T>>;
};

// TODO: Revist this type gymnastics
const firstWorkspaceCapabilityValidator = workspaceCapabilityValidatorMap[WorkspaceCapabilityType.ottopilotAnnouncements];
const remainingWorkspaceCapabilityValidators = Object.values(workspaceCapabilityValidatorMap).filter(
    (validator) => validator !== firstWorkspaceCapabilityValidator,
);

export const workspaceCapabilityValidator: zod.ZodType<AnyWorkspaceCapability> = zod.discriminatedUnion("type", [
    firstWorkspaceCapabilityValidator,
    ...remainingWorkspaceCapabilityValidators,
]);

export type WorkspaceCapabilityCudLog<T extends WorkspaceCapabilityType = WorkspaceCapabilityType> = WorkspaceCapability<T>;

export const workspaceCapabilityCudLogValidator = workspaceCapabilityValidator;

const workspaceCapabilityTypeValidator = zod.enum(WorkspaceCapabilityType);

// Capability types can be added independently by API producers. Ignore only capabilities whose
// discriminant is unknown to this version; known capabilities remain strictly validated.
export const workspaceCapabilitiesValidator = zod.preprocess(
    (input) => {
        if (Array.isArray(input) !== true) {
            return input;
        }

        return input.filter((candidate) => {
            if (typeof candidate !== "object" || candidate === null || !("type" in candidate)) {
                return true;
            }

            const type = candidate.type;
            return typeof type !== "string" || workspaceCapabilityTypeValidator.safeParse(type).success === true;
        });
    },
    zod.array(workspaceCapabilityValidator),
);

// TODO: Should we move these to pluto as this a very specific pluto requirement?
export type WorkspaceCapabilityProvisionInput = {
    [T in WorkspaceCapabilityType]: Pick<WorkspaceCapability<T>, "configuration" | "type">;
}[WorkspaceCapabilityType];

export const workspaceCapabilityProvisionValidator: zod.ZodType<WorkspaceCapabilityProvisionInput> = zod.discriminatedUnion("type", [
    firstWorkspaceCapabilityValidator.pick({configuration: true, type: true}),
    ...remainingWorkspaceCapabilityValidators.map((validator) => validator.pick({configuration: true, type: true})),
]);

/** Explicit ownership only; disabling a capability does not release its UDO objects. */
export function getWorkspaceCapabilityOwnedUdoTemplateIds(capabilities: ReadonlyArray<Pick<WorkspaceCapability, "type" | "udoBindings">>): ReadonlySet<Uuid> {
    const ids = new Set<Uuid>();
    for (const capability of capabilities) {
        const owned = Object.values(capability.udoBindings.fixedUdoTemplates).map(binding => binding.udoTemplateId);
        if (capability.type === WorkspaceCapabilityType.ottopilotEventContainers) {
            const bindings = workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotEventContainers].parse(capability.udoBindings);
            owned.push(...Object.values(bindings.eventContainerUdoTemplates).map(binding => binding.udoTemplateId));
        } else if (capability.type === WorkspaceCapabilityType.ottopilotEvents) {
            const bindings = workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.ottopilotEvents].parse(capability.udoBindings);
            owned.push(...Object.values(bindings.eventUdoTemplates).map(binding => binding.udoTemplateId));
        } else if (capability.type === WorkspaceCapabilityType.tuffexProductCatalogue) {
            const bindings = workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.tuffexProductCatalogue].parse(capability.udoBindings);
            owned.push(...Object.values(bindings.catalogueUdoTemplates).map(binding => binding.udoTemplateId));
        } else if (capability.type === WorkspaceCapabilityType.retailLocations) {
            const bindings = workspaceCapabilityUdoBindingsValidatorMap[WorkspaceCapabilityType.retailLocations].parse(capability.udoBindings);
            owned.push(...Object.values(bindings.retailLocationUdoTemplates).map(binding => binding.udoTemplateId));
        }
        for (const id of owned) {
            ids.add(uuidZodValidator.parse(id.toLowerCase()));
        }
    }
    return ids;
}
